import {useTranslationContext} from 'platform/components';

import {useCallback, useEffect, useRef, useState} from 'react';

import {isNilOrEmpty} from 'ramda-adjunct';

import {isNotNilOrEmpty, useBoolean} from 'shared/web';

import {useHttpCalls} from '../api/useHttpCalls';
import {selectChoiceFilterOptions, selectGridCode} from '../store/createEagGridStore';
import {useEagGridSelector} from '../store/useEagGridSelector';
import {useEagGridStore} from '../store/useEagGridStore';
import type {ChoiceFilterOptions} from '../types/Api';

const EMPTY_OPTIONS: ChoiceFilterOptions = [];

type UseLazyChoiceFilterOptionsProps = {
  columnKey: string;
};

/**
 * Lazily loads choice filter options and caches them in EagGrid store state.
 * Cached options survive panel close/reopen — no redundant network requests.
 *
 * `loadOptions` has a **stable identity** (never changes between renders) because
 * its only dependency is `store` (ref-stable). All volatile values (gridCode,
 * columnKey, http, cachedOptions) are read at call time via `store.getState()`
 * and `httpRef.current` — not captured in the closure.
 *
 * @see Vercel advanced-event-handler-refs pattern
 */
export function useLazyChoiceFilterOptions(props: UseLazyChoiceFilterOptionsProps) {
  const store = useEagGridStore();
  const cachedOptions = useEagGridSelector(selectChoiceFilterOptions(props.columnKey));
  const http = useHttpCalls();
  const t = useTranslationContext();

  const [isLoading, startLoading, stopLoading] = useBoolean();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Refs hold latest volatile values so loadOptions can read them at call time
  // without depending on their identity (http and t change every render).
  const httpRef = useRef(http);
  const columnKeyRef = useRef(props.columnKey);
  const tRef = useRef(t);

  // eslint-disable-next-line no-restricted-syntax -- intentional: no-deps useEffect syncs refs every render
  useEffect(() => {
    httpRef.current = http;
    columnKeyRef.current = props.columnKey;
    tRef.current = t;
  });

  /**
   * @mandatory Consumers call `loadOptions` in `useEffect([loadOptions])` — the effect must
   * fire only once on mount. Without stable identity (useCallback with stable deps), the
   * effect would re-run on every render, causing an infinite fetch loop.
   * All volatile values (gridCode, http, columnKey, cachedOptions) are read via refs /
   * store.getState() at call time — not captured in the closure.
   */
  const loadOptions = useCallback(() => {
    const gridCode = selectGridCode(store.getState());
    const columnKey = columnKeyRef.current;
    const cached = selectChoiceFilterOptions(columnKey)(store.getState());

    if (isNilOrEmpty(gridCode) || isNilOrEmpty(columnKey)) {
      return Promise.resolve(EMPTY_OPTIONS);
    }

    if (isNotNilOrEmpty(cached)) {
      setErrorMessage(null);
      stopLoading();
      return Promise.resolve(cached as ChoiceFilterOptions);
    }

    startLoading();
    setErrorMessage(null);

    return httpRef.current
      .getColumnFilterOptions(columnKey)
      .then((response) => {
        const options = response ?? EMPTY_OPTIONS;
        setErrorMessage(null);
        store.setChoiceFilterOptions(columnKey, options);
      })
      .catch(() => {
        setErrorMessage(tRef.current('general.errors.cannotLoadOptions'));
      })
      .finally(() => {
        stopLoading();
      });
  }, [store, startLoading, stopLoading]);

  return {
    options: cachedOptions ?? EMPTY_OPTIONS,
    isLoading,
    errorMessage,
    loadOptions,
  };
}
